import {
  Container,
  Graphics,
  Text
} from 'pixi.js'
import {TimelineTimestamp} from 'storyboard-renderer'
import {TimeIndicator} from './time-indicator'

export class Timebar extends Container{

  protected timeIndicator: TimeIndicator;
  protected container: Container;
  protected sectionHeight: number = 36;
  protected highlightedUntil: number = 0;
  /**
   * milyen hosszú legyen egy szakasz (másodpercben)
   */
  protected sectionLengthInSeconds = 10;
  /**
   * ha valamelyik mezőnek ezek közül megváltozik az értéke, akkor újra kell renderelni
   */
  protected cache;
  /**
   * a konkrét timebar container, hogy updatelhessük ha kellene
   */
  protected timebar: Container;
  /**
   * minimum milyen széles legyen a timebar (másodpercben)
   *
   * ez azért kell, hogy ha nagyon rövid timeline-unk lenne, akkor is minimum a képernyő széléig tartson a timebar
   *
   * @todo ne csak beégetni, hanem dinamikusan számolni a képernyő szélésségéből és a timeToPx()-ből
   *
   * fullhd képernyőn 20%-os (minimális) zoomon szűk 5 perc fér ki
   *
   */
  public minimumWidthInSeconds:number = 5 * 60;

  constructor(timeIndicator?:TimeIndicator){
    super();
    if (timeIndicator){
      this.setTimeIndicator(timeIndicator);
    }
    this.container = new Container();
    this.addChild(this.container);
    if (!this.cache){
      this.cache = {};
    }
  }

  setHighlightedUntil(x: number){
    this.highlightedUntil = x;
  }

  setTimeIndicator(value: TimeIndicator){
    this.timeIndicator = value;
  }

  public recalculate(){
    if (this.timeIndicator){
      if (!this.timebar){
        this.timebar = new Container();
      }
      this.container.addChild(this.recalculateTimebar(this.timebar));
    }
  }
  /**
   * hány section-nek kell megjelennie a képernyőn?
   *
   * van minimuma is, hogy tutira kiérjen a képernyő széléig
   */
  protected calculateExactSectionCount():number{
    let widthInSeconds = this.timeIndicator.getMaxTime().inSeconds();
    if (widthInSeconds < this.minimumWidthInSeconds){
      widthInSeconds = this.minimumWidthInSeconds;
    }
    return widthInSeconds / this.sectionLengthInSeconds;
  }
  /**
   * milyen széles egy section pixelben (zoomtól függően változhat)
   */
  protected calculateSectionWidth():number{
    let widthInSeconds = this.timeIndicator.getMaxTime().inSeconds();
    let exactSectionCount = widthInSeconds / this.sectionLengthInSeconds;
    return (this.timeIndicator.getMaxPositionX() / exactSectionCount);
  }

  protected shouldUpdate():boolean{
    if (!this.cache){
      this.cache = {};
    }
    return this.shouldUpdateSectionCount() || this.shouldUpdateMaxPositionX();
  }

  protected shouldUpdateSectionCount(){
    let value = this.calculateExactSectionCount();
    let ret = true;
    if (this.cache.lastExactSectionCount){
      ret = value.toFixed(3) !== this.cache.lastExactSectionCount.toFixed(3);
    }
    this.cache.lastExactSectionCount = value;
    return ret;
  }

  protected shouldUpdateMaxPositionX(){
    let value = this.timeIndicator.getMaxPositionX();
    let ret = true;
    if (this.cache.getMaxPositionX){
      ret = value.toFixed(3) !== this.cache.getMaxPositionX.toFixed(3);
    }
    this.cache.getMaxPositionX = value;
    return ret;
  }

  protected shouldUpdateHighlightedUntil(){
    let value = this.highlightedUntil;
    let ret = true;
    if (this.cache.highlightedUntil){
      ret = value.toFixed(3) !== this.cache.highlightedUntil.toFixed(3);
    }
    this.cache.highlightedUntil = value;
    return ret;
  }

  protected shouldUpdateSectionAtIndex(sections: Container, index: number, sectionStartTime: TimelineTimestamp, sectionWidth: number, highlightedUntilPosition: number):boolean{
    let s = sections.getChildAt(index);
    return (
      (!(s["sectionStartTime"].equals(sectionStartTime))) ||
      ((s["sectionWidth"] != sectionWidth)) ||
      ((s["highlightedUntilPosition"] != highlightedUntilPosition))
    );
  }

  protected recalculateTimebar(timebar:Container):Container{
    let ret = timebar;
    /**
     * egészen pontosan hány ilyen szakasz fog kiférni
     */
    let exactSectionCount = this.calculateExactSectionCount();
    /**
     * és hányat kell lerenderelni
     */
    let sectionCount = Math.ceil(exactSectionCount);
    /**
     * milyen széles egy section pixelben (zoomtól függően változhat)
     */
    let sectionWidth = this.calculateSectionWidth();

    let shouldUpdate = this.shouldUpdateSectionCount();

    for (let i = 0; i < sectionCount; i++){
      /**
       * hol kezdődik a section pixelben
       */
      let sectionStartPosition = i * sectionWidth;
      /**
       * hol kezdődik a section időben
       */
      let sectionStartTime = TimelineTimestamp.fromMilliseconds(i * 10 * 1000);
      /**
       * ha már elhagyta a lejátszás az adott section legelejét, akkor számoljuk ki, hogy
       * mennyi részt kell kékre színezni
       */
      let highlightedUntilPosition = this.highlightedUntil - sectionStartPosition;
      if (highlightedUntilPosition < 0){
        highlightedUntilPosition = 0;
      }

      if (shouldUpdate){
        let section = this.createSection(sectionStartTime, sectionWidth, highlightedUntilPosition);
        section.position.x = sectionStartPosition;
        ret.addChild(section);
      }else {
        if (this.shouldUpdateSectionAtIndex(ret, i, sectionStartTime, sectionWidth, highlightedUntilPosition)){
          let section = this.createSection(sectionStartTime, sectionWidth, highlightedUntilPosition);
          section.position.x = sectionStartPosition;
          (<Container>ret.children[i]).destroy({
            children: true
          });
          ret.addChildAt(section, i);
        }
      }
    }

    return ret;
  }

  protected createSection(time: TimelineTimestamp, sectionWidth: number, highlightedUntil:number):Container{
    let ret = new Container();

    let backgroundColor = new Graphics();
    backgroundColor.beginFill(0xd4dce3);
    backgroundColor.drawRect(0,0, sectionWidth, 36);
    ret.addChild(backgroundColor);

    if (highlightedUntil > sectionWidth){
      highlightedUntil = sectionWidth;
    }

    let highlightedBackground = new Graphics();
    highlightedBackground.beginFill(0xb2d8e6);
    highlightedBackground.drawRect(0,0,highlightedUntil, 36);
    ret.addChild(highlightedBackground);
    /**
     * a 10 másodpercenkénti bullet
     */
    let pointIndicator = new Graphics();
    pointIndicator.beginFill(0xdfe5ea);
    pointIndicator.drawRect(0,0,3,3);
    pointIndicator.beginFill(0xffffff);
    pointIndicator.drawRect(0,1,3,1);
    pointIndicator.drawRect(1,0,1,3);
    pointIndicator.position.y = this.sectionHeight / 2 - 1;
    ret.addChild(pointIndicator);

    /**
     * az 5 másodpercenkénti bullet-et mindig az adott section feléhez rajzoljuk
     *
     * de csak akkor, ha a section maga van elég széles ahhoz, hogy legyen is értelme kirajzolni
     */
    if (sectionWidth > 50) {
      let pointIndex = 1;
      // Keep at least 50px separation
      let maxDivision = Math.floor(sectionWidth / 50);

      // Round down to a divisor of 10 - [10, 5, 2, 1]
      while (10 % maxDivision !== 0) {
        maxDivision --;
      }

      // Draw points every 1/10, 1/5 or 1/2 width (1/1 point is already marked)
      for (pointIndex; pointIndex < maxDivision; pointIndex ++) {
        let pointMark = new Graphics();
        pointMark.beginFill(0xe4e9ed);
        pointMark.drawRect(0,0,3,3);
        pointMark.beginFill(0xe4e9ed);
        pointMark.drawRect(0,1,3,1);
        pointMark.drawRect(1,0,1,3);
        pointMark.position.y = this.sectionHeight / 2 - 1;
        pointMark.position.x = sectionWidth * (pointIndex / maxDivision);
        ret.addChild(pointMark);
      }
    }

    const fontSize = 12;

    let text = new Text(time.asMinutesSecondsString(), {
      fontFamily: 'Arial',
      fontSize: fontSize,
      fill: 0xFFFFFF,
      fontWeight: 'bold',
    });

    text.position.x = 6;
    text.position.y = 12;

    ret.addChild(text);

    ret['sectionStartTime'] = time;
    ret['sectionWidth'] = sectionWidth;
    ret['highlightedUntilPosition'] = highlightedUntil;

    return ret;
  }

  public ticker(delta?: number){
    this.recalculate();
  }
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/timeline/audio-track/waveform/waveform-renderer/timebar.ts